import { useMutation } from '@apollo/client';
import {
  Stack,
  Heading,
  Text,
  ButtonIcon,
  Card,
  Column,
  Columns,
  TextLink,
  IconHeart,
  IconDelete,
  useResponsiveValue,
  Link,
  Inline,
  Badge,
  Box,
  TooltipRenderer,
  useToast,
  Loader,
} from 'braid-design-system';
import { DateTime } from 'luxon';
import React from 'react';

import { configValues } from 'src/config';
import {
  DeleteSavedListingDocument,
  GetSavedListingsDocument,
  type SavedListing,
} from 'src/generated/graphql';
import { decodeSpecialChar } from 'src/utils/decode';
import { dateDiff } from 'src/utils/durationText';

interface Props {
  listing: SavedListing;
}

const SavedItem = ({ listing }: Props) => {
  const [deleteSavedListing, { loading }] = useMutation(
    DeleteSavedListingDocument,
    {
      refetchQueries: [{ query: GetSavedListingsDocument }],
    },
  );
  const showToast = useToast();

  const deleteListing = async (listingId: string) => {
    try {
      await deleteSavedListing({ variables: { input: { listingId } } });

      showToast({
        data: { testId: 'savedListingDeletedToast' },
        tone: 'positive',
        message: 'Saved listing deleted',
      });
    } catch {
      showToast({
        tone: 'critical',
        message: 'Delete saved listing error',
        description:
          'Something went wrong while deleting your saved listing. Please try again.',
      });
    }
  };

  const responsiveValue = useResponsiveValue();
  const isMobile =
    responsiveValue({
      mobile: true,
      tablet: false,
    }) ?? false;

  const url = `${configValues.HEADER_SERVER_BASE}/business-listing/${listing.urlKey}/${listing.listingId}`;
  const tag = [
    listing.opportunityType === 'Franchise New'
      ? 'New Franchise'
      : listing.opportunityType,
  ];
  return (
    <Box id={`listing-card-${listing.listingId}`}>
      <Card>
        <Columns space="medium">
          {!isMobile ? (
            <Column width="1/4">
              <Link href={url}>
                <img
                  id="listing-image"
                  alt={listing.businessName}
                  src={listing.imageUrl}
                  width="100%"
                />
              </Link>
            </Column>
          ) : null}

          <Column>
            <Stack space="gutter">
              <Stack space="small">
                <Inline space="small">
                  {DateTime.fromISO(listing.expiryDate)
                    .diffNow()
                    .as('minutes') < 0 ? (
                    <TooltipRenderer
                      id="listing-expired-tooltip"
                      tooltip={
                        <Text>
                          This listing has expired but may be reposted
                        </Text>
                      }
                    >
                      {({ triggerProps }) => (
                        <Box {...triggerProps}>
                          <Badge id="listing-expired-badge" tone="neutral">
                            Expired
                          </Badge>
                        </Box>
                      )}
                    </TooltipRenderer>
                  ) : null}

                  <Heading level="3">
                    <TextLink id="listing-title" href={url} hitArea="large">
                      {decodeSpecialChar(listing.title)}
                    </TextLink>
                  </Heading>
                </Inline>
                <Text id="listing-businessname" tone="secondary">
                  {decodeSpecialChar(listing.businessName)}
                </Text>
              </Stack>

              {isMobile ? (
                <Link href={url}>
                  <img
                    src={listing.imageUrl}
                    alt={listing.businessName}
                    id="saved-listing-image"
                  />
                </Link>
              ) : null}

              <Box>
                {/* Listing Details */}
                <Stack space="small">
                  <Text weight="strong" id="listing-location">
                    {/* <IconLocation /> &nbsp; */}
                    {decodeSpecialChar(listing.location)}
                  </Text>
                  <Text weight="strong" id="listing-price">
                    {/* <IconMoney /> &nbsp; */}
                    {listing.price}
                  </Text>
                  <Text weight="strong" id="listing-tag">
                    {/* <IconTag /> &nbsp; */}
                    {tag}
                  </Text>
                </Stack>

                <Box paddingTop="medium">
                  <Text tone="brandAccent" size="small">
                    <IconHeart /> Saved&nbsp;{dateDiff(listing.savedDate ?? '')}
                  </Text>
                </Box>
              </Box>
            </Stack>
          </Column>

          <Column width="1/5">
            <Stack space="gutter" align="right">
              {loading ? (
                <Loader />
              ) : (
                <ButtonIcon
                  id={`delete-saved-listing-btn-${listing.listingId}`}
                  onClick={() => deleteListing(listing.listingId)}
                  icon={<IconDelete tone="critical" />}
                  label="Delete saved business"
                />
              )}
            </Stack>
          </Column>
        </Columns>
      </Card>
    </Box>
  );
};

export default SavedItem;
